import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Radar, Search, Loader2, AlertTriangle, CheckCircle2, XCircle,
  ArrowRight, Sparkles, Gauge, Lightbulb, Target, Shield, TrendingUp,
} from "lucide-react";
import { siInstantAudit, siPublicSelfAudit } from "@/lib/api";
import { toast } from "sonner";
import InfographicHero from "@/components/InfographicHero";

/**
 * WebsiteTech Search Intelligence™ — public landing (`/si`).
 * Positions the redesigned SEO product: Discover → Diagnose → Fix → Verify.
 * Hosts the free single-page audit and links to methodology + sample report.
 */

const ScoreTile = ({ label, value, tone = "slate", suffix = "/100" }) => {
  const toneMap = {
    good: "text-emerald-600 border-emerald-200 bg-emerald-50",
    warn: "text-amber-600 border-amber-200 bg-amber-50",
    crit: "text-rose-600 border-rose-200 bg-rose-50",
    slate: "text-slate-700 border-slate-200 bg-white",
  };
  const cls = toneMap[tone] || toneMap.slate;
  return (
    <div className={`rounded-2xl border p-5 ${cls}`} data-testid={`si-score-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs font-semibold uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-2 font-heading text-4xl font-black tabular-nums">
        {value}<span className="text-lg font-medium opacity-60">{suffix}</span>
      </div>
    </div>
  );
};

const toneFor = (v) => (v >= 75 ? "good" : v >= 50 ? "warn" : "crit");

const IssuePill = ({ code, severity, url, evidence }) => {
  const cls =
    severity === "critical" ? "border-rose-200 bg-rose-50 text-rose-700"
    : severity === "warning" ? "border-amber-200 bg-amber-50 text-amber-800"
    : "border-slate-200 bg-slate-50 text-slate-700";
  const Icon = severity === "critical" ? XCircle : severity === "warning" ? AlertTriangle : CheckCircle2;
  return (
    <div className={`rounded-xl border px-4 py-3 ${cls}`} data-testid={`si-issue-${code}`}>
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            <span>{code.replace(/_/g, " ")}</span>
            <Badge variant="outline" className="text-[10px] uppercase">{severity}</Badge>
          </div>
          <div className="mt-1 text-xs opacity-80">{evidence}</div>
          {url && <div className="mt-1 truncate font-mono text-[11px] opacity-60">{url}</div>}
        </div>
      </div>
    </div>
  );
};

export default function SearchIntelligenceLanding() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [selfAudit, setSelfAudit] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    siPublicSelfAudit()
      .then(setSelfAudit)
      .catch(() => setSelfAudit(null));
  }, []);

  const run = async () => {
    const u = url.trim();
    if (!u) return;
    setLoading(true); setErr(""); setResult(null);
    try {
      const r = await siInstantAudit(u);
      setResult(r);
      if (!r.ok) setErr(r.reason || "Unable to reach the URL.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Instant audit failed. Try another URL.");
      toast.error("Audit failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="si-landing">
      <Navbar />

      {/* Hero */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Radar className="h-4 w-4" /> WebsiteTech Search Intelligence™
          </div>
          <h1 className="mt-4 font-heading text-4xl font-black leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
            From SEO audit to <span className="text-orange-600">verified improvement.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-slate-600 sm:text-lg">
            Know what's wrong. Know what matters. Know whether the fix worked.
            One platform that carries every opportunity from Discover → Fix → Verify.
          </p>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-6">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Run a free instant SEO check — no signup
            </label>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <Input
                data-testid="si-instant-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://your-site.com"
                className="h-12 flex-1 bg-white text-base"
              />
              <Button
                data-testid="si-instant-run"
                onClick={run}
                disabled={loading || !url.trim()}
                className="h-12 bg-orange-600 px-6 text-white hover:bg-orange-700"
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                Run instant check
              </Button>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
              <span>· SSRF-hardened crawler</span>
              <span>· Robots.txt respected</span>
              <span>· Methodology fully disclosed</span>
            </div>
          </div>

          {err && (
            <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="si-error">
              {err}
            </div>
          )}
        </div>
      </section>

      {/* Instant results */}
      {result?.ok && (
        <section className="border-b border-slate-200 bg-slate-50" data-testid="si-instant-results">
          <div className="mx-auto max-w-6xl px-4 py-12">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-2xl font-bold text-slate-900">Instant preview</h2>
                <p className="text-sm text-slate-500">
                  Single-page snapshot · Data Confidence: <b>{result.scores.confidence_label}</b>. Run a full crawl for site-wide scores.
                </p>
              </div>
              <Button asChild variant="outline" data-testid="si-methodology-link">
                <Link to="/si/methodology">See how we score →</Link>
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <ScoreTile label="Readiness" value={result.scores.readiness} tone={toneFor(result.scores.readiness)} />
              <ScoreTile label="Technical Health" value={result.scores.technical_health} tone={toneFor(result.scores.technical_health)} />
              <ScoreTile label="Business Alignment" value={result.scores.business_alignment} tone={toneFor(result.scores.business_alignment)} />
              <ScoreTile label="Data Confidence" value={result.scores.data_confidence} tone={toneFor(result.scores.data_confidence)} />
            </div>

            <div className="mt-8">
              <h3 className="font-heading text-lg font-bold text-slate-900">
                {result.issues.length} findings on this page
              </h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {result.issues.slice(0, 10).map((i, idx) => (
                  <IssuePill key={idx} {...i} />
                ))}
              </div>
              {result.issues.length === 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                  No critical issues detected on this page. Run a full crawl to check the rest of the site.
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Self-audit trend badges — we dogfood the product on our own site */}
      {selfAudit?.available && (
        <section className="border-b border-orange-200 bg-gradient-to-r from-orange-50 via-amber-50 to-sky-50" data-testid="si-self-audit">
          <div className="mx-auto max-w-6xl px-4 py-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-700">
                  <TrendingUp className="h-4 w-4" /> We dogfood our own tool
                </div>
                <h2 className="mt-2 font-heading text-2xl font-black text-slate-900 sm:text-3xl">
                  Live Search Intelligence scores for <span className="text-orange-600">websitetech.pro</span>
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-slate-600">
                  Trend badges only — no client-specific findings exposed. Full report is available to signed-in operators.
                  Methodology version <b>{selfAudit.methodology_version}</b>.
                </p>
              </div>
              <Link to="/si/methodology" className="text-sm font-semibold text-orange-700 hover:underline" data-testid="si-self-audit-methodology-link">
                See scoring method <ArrowRight className="inline h-3 w-3" />
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
              {[
                ["Readiness", selfAudit.scores.readiness],
                ["Technical", selfAudit.scores.technical_health],
                ["Opportunity", selfAudit.scores.search_opportunity],
                ["Business", selfAudit.scores.business_alignment],
                ["Confidence", selfAudit.scores.data_confidence],
              ].map(([l, v]) => (
                <div key={l} className={`rounded-2xl border bg-white p-4 shadow-sm ${v >= 75 ? "border-emerald-200" : v >= 50 ? "border-amber-200" : "border-rose-200"}`} data-testid={`si-self-badge-${l.toLowerCase()}`}>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{l}</div>
                  <div className={`mt-1 font-heading text-3xl font-black tabular-nums ${v >= 75 ? "text-emerald-700" : v >= 50 ? "text-amber-700" : "text-rose-700"}`}>
                    {v ?? "—"}<span className="text-sm font-medium opacity-60">/100</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Confidence label: <b>{selfAudit.scores.confidence_label}</b> · Refreshed weekly
            </div>
          </div>
        </section>
      )}

      {/* Value props */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-4 pt-14">
          <InfographicHero slug="website-analysis" hint={false} />
        </div>
        <div className="mx-auto max-w-6xl px-4 py-16">
          <h2 className="font-heading text-3xl font-black text-slate-900 sm:text-4xl">
            Four transparent scores. No opaque "Google score."
          </h2>
          <p className="mt-3 max-w-2xl text-slate-600">
            Every input, weight and limitation is disclosed on the methodology page.
            Confidence rises as you connect Search Console, GA4 and PageSpeed.
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Gauge,    title: "Technical Health",      body: "Are important URLs crawlable, indexable and error-free?" },
              { icon: Lightbulb, title: "Search Opportunity",    body: "How much traffic upside if the gaps get fixed?" },
              { icon: Target,   title: "Business Alignment",     body: "Do priority pages support the services that matter?" },
              { icon: Shield,   title: "Data Confidence",        body: "How much of your analytical picture is connected?" },
            ].map(({ icon: Icon, title, body }) => (
              <Card key={title} className="border-slate-200 bg-white p-6" data-testid={`si-prop-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon className="h-6 w-6 text-orange-600" />
                <div className="mt-3 font-heading text-lg font-bold text-slate-900">{title}</div>
                <div className="mt-1 text-sm text-slate-600">{body}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-links to sample + methodology + console */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 py-16 md:grid-cols-3">
          <Link to="/si/sample" className="block" data-testid="si-cta-sample">
            <Card className="h-full border-slate-200 bg-white p-6 transition hover:border-orange-300 hover:shadow-md">
              <Sparkles className="h-6 w-6 text-orange-600" />
              <div className="mt-3 font-heading text-lg font-bold">See a sample report</div>
              <div className="mt-1 text-sm text-slate-600">A realistic before-and-after for a fictional clinic — clearly labelled demonstration data.</div>
              <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-orange-600">View sample <ArrowRight className="h-4 w-4" /></div>
            </Card>
          </Link>
          <Link to="/si/methodology" className="block" data-testid="si-cta-methodology">
            <Card className="h-full border-slate-200 bg-white p-6 transition hover:border-orange-300 hover:shadow-md">
              <Radar className="h-6 w-6 text-orange-600" />
              <div className="mt-3 font-heading text-lg font-bold">Read the methodology</div>
              <div className="mt-1 text-sm text-slate-600">Every input, weight and limitation — no black-box scoring.</div>
              <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-orange-600">See how it works <ArrowRight className="h-4 w-4" /></div>
            </Card>
          </Link>
          <Link to="/si/console" className="block" data-testid="si-cta-console">
            <Card className="h-full border-slate-200 bg-white p-6 transition hover:border-orange-300 hover:shadow-md">
              <Search className="h-6 w-6 text-orange-600" />
              <div className="mt-3 font-heading text-lg font-bold">Open the Search Intelligence console</div>
              <div className="mt-1 text-sm text-slate-600">Track full sites, run crawls, review findings and ask the SEO Analyst.</div>
              <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-orange-600">Sign in and start <ArrowRight className="h-4 w-4" /></div>
            </Card>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
